import {
  DocGenerationRequestSummaryDto,
  DocumentGenerationStatusDto,
  ScenarioTransactionDto,
  ScenarioTransactionRemovedDto,
  ScenarioTransactionResultDto,
  ScenarioUpdateDto,
} from '@/api/hub/scenario/list.schema'
import { HubService } from '@/api/hub/signalr'

class ScenarioService extends HubService {
  constructor() {
    super()
  }

  /**
   * Initiates a SignalR hub connection for scenario management.
   * The connection is made to the '/scenario' endpoint.
   *
   * Optionally, you can provide a callback to be invoked after the connection is established.
   */
  startHubConnection(onConnected?: () => void) {
    this.hub.startConnection('/scenario-hub', () => {
      if (onConnected) onConnected()
    })
  }

  /**
   * Creates a new scenario with the given forecast ID and name.
   * @param forecastId - The ID of the forecast to base the scenario on
   * @param name - The name of the new scenario
   */
  createScenario(forecastId: string, name: string) {
    this.hub.sendMessage('CreateScenario', forecastId, name)
  }

  /**
   * Joins a specific scenario room to receive updates for that scenario.
   * @param scenarioId - The ID of the scenario to join
   */
  joinScenario(scenarioId: string) {
    this.hub.sendMessage('JoinScenario', scenarioId)
  }

  /**
   * Leaves a specific scenario room to stop receiving updates for that scenario.
   * @param scenarioId - The ID of the scenario to leave
   */
  leaveScenario(scenarioId: string) {
    this.hub.sendMessage('LeaveScenario', scenarioId)
  }

  /**
   * Adds a transaction to the specified scenario.
   * @param scenarioId - The ID of the scenario to add the transaction to
   * @param transaction - The transaction data to add
   */
  addTransaction(
    scenarioId: string,
    transaction: Omit<ScenarioTransactionDto, 'id'>
  ) {
    this.hub.sendMessage('AddTransaction', scenarioId, transaction)
  }

  /**
   * Removes a transaction from the specified scenario.
   * @param scenarioId - The ID of the scenario to remove the transaction from
   * @param transactionId - The ID of the transaction to remove
   */
  removeTransaction(scenarioId: string, transactionId: string) {
    this.hub.sendMessage('RemoveTransaction', scenarioId, transactionId)
  }

  /**
   * Updates the name of the specified scenario.
   * @param scenarioId - The ID of the scenario to update
   * @param name - The new name for the scenario
   */
  updateScenarioName(scenarioId: string, name: string) {
    this.hub.sendMessage('UpdateScenarioName', scenarioId, name)
  }

  /**
   * Requests generation of documents for the specified scenario.
   * @param scenarioId - The ID of the scenario to generate documents for
   * @param selectedTransactionIds - Array of transaction IDs to include in the document generation
   */
  requestDocumentGeneration(
    scenarioId: string,
    selectedTransactionIds: string[]
  ) {
    this.hub.sendMessage(
      'RequestDocumentGeneration',
      scenarioId,
      selectedTransactionIds
    )
  }

  /**
   * Registers a callback function to handle error messages from the hub.
   * @param callback - Function to be called when an error message is received
   */
  handleError(callback: (message: string) => void) {
    this.hub.onReceiveMessage('Error', callback)
  }

  /**
   * Registers a callback function to handle scenario updates from the hub.
   * @param callback - Function to be called when a scenario is updated
   */
  handleScenarioUpdated(callback: (update: ScenarioUpdateDto) => void) {
    this.hub.onReceiveMessage('ScenarioUpdated', callback)
  }

  /**
   * Registers a callback function to handle transaction added events from the hub.
   * @param callback - Function to be called when a transaction is added
   */
  handleTransactionAdded(
    callback: (result: ScenarioTransactionResultDto) => void
  ) {
    this.hub.onReceiveMessage('TransactionAdded', callback)
  }

  /**
   * Registers a callback function to handle transaction removed events from the hub.
   * @param callback - Function to be called when a transaction is removed
   */
  handleTransactionRemoved(
    callback: (result: ScenarioTransactionRemovedDto) => void
  ) {
    this.hub.onReceiveMessage('TransactionRemoved', callback)
  }

  /**
   * Registers a callback function to handle scenario name updates from the hub.
   * @param callback - Function to be called when a scenario name is updated
   */
  handleScenarioNameUpdated(
    callback: (scenarioId: string, newName: string) => void
  ) {
    this.hub.onReceiveMessage('ScenarioNameUpdated', callback)
  }

  /**
   * Registers a callback function to handle document generation status updates from the hub.
   * @param callback - Function to be called when the document generation status is updated
   */
  handleDocumentGenerationUpdated(
    callback: (status: DocumentGenerationStatusDto) => void
  ) {
    this.hub.onReceiveMessage('DocumentGenerationUpdated', callback)
  }

  // New simplified handlers for refactored scenario flow
  /**
   * Registers a callback function to handle scenario transactions loaded from the hub.
   * This is called when joining a scenario and returns only the scenario transactions.
   * @param callback - Function to be called when scenario transactions are loaded
   */
  handleScenarioTransactionsLoaded(
    callback: (transactions: ScenarioTransactionDto[]) => void
  ) {
    this.hub.onReceiveMessage('ScenarioTransactionsLoaded', callback)
  }

  /**
   * Registers a callback function to handle scenario transactions updates from the hub.
   * This is called after add/remove transaction operations.
   * @param callback - Function to be called when scenario transactions are updated
   */
  handleScenarioTransactionsUpdated(
    callback: (transactions: ScenarioTransactionDto[]) => void
  ) {
    this.hub.onReceiveMessage('ScenarioTransactionsUpdated', callback)
  }

  /**
   * Registers a callback function to handle document generation requests loaded from the hub.
   * This is called when joining a scenario and returns the history of document generation requests.
   * @param callback - Function to be called when document generation requests are loaded
   */
  handleDocumentGenerationRequestsLoaded(
    callback: (requests: DocGenerationRequestSummaryDto[]) => void
  ) {
    this.hub.onReceiveMessage('DocumentGenerationRequestsLoaded', callback)
  }
}

export const scenarioHubService = new ScenarioService()
